import { useRouter } from 'expo-router'
import { useEffect, useState, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { ActivityIndicator, View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Image } from '@/src/elements/image'
import { useNotificationSettingsMutation } from '@/src/features/notification/services/useNotificationSettingsMutation'
import { PushRequest } from '@/src/features/notification/ui/components/PushRequest'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { GTM } from '@/src/general/utils/gtm'
import { EVENTS } from '@/src/general/utils/gtm/constants'
import { parseIdDash } from '@/src/general/utils/parseIdDash'
import { getLocale } from '@/src/lang/getLocale'

import { NOTIFICATION_CATEGORY, PUSH_EVENT_LABEL } from '../../../notification/models/notification.constants'
import { useCommunityQuery } from '../../services/useCommunityQuery'
import { useJoinCommunityMutation } from '../../services/useJoinCommunity'
import { useCommunityJoinSteps } from '../hooks/useCommunityJoinSteps'
import { SubscribeJourneyLayout } from '../layouts/SubscribeJourneyLayout'

const notificationsIllustrationByLocale = {
  en: require('@/img/community/notifications/en.png'),
  fr: require('@/img/community/notifications/fr.png'),
  es: require('@/img/community/notifications/es.png'),
  pt: require('@/img/community/notifications/pt.png'),
}

export const CommunityNotificationPermissionsScreen = ({
  searchParams,
  idSlug,
}: TSearchParamsParam & TIdSlugParam): ReactNode => {
  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(0)
  const locale = getLocale()
  const router = useRouter()
  const [id] = parseIdDash(idSlug)

  const { data: community } = useCommunityQuery(id)
  const { goToNextStep, isPending, shouldBeDisplayed, stepperOptions } = useCommunityJoinSteps({
    idSlug,
    stepName: 'push',
    searchParams,
  })

  const {
    join,
    isPending: isJoinPending,
    isSuccess: isJoinSuccess,
    isError: isJoinError,
  } = useJoinCommunityMutation(id, searchParams)

  useEffect(() => {
    if (community != null && !community.hasJoined && !isJoinPending && !isJoinSuccess && !isJoinError) {
      join('ongoing')
    }
  }, [community, join, isJoinPending, isJoinSuccess, isJoinError])

  const imageSource =
    locale === 'pl' || locale === 'it'
      ? notificationsIllustrationByLocale['en']
      : notificationsIllustrationByLocale[locale]

  const { mutate: editNotificationSettings } = useNotificationSettingsMutation(NOTIFICATION_CATEGORY.PUBLICATIONS)

  useEffect(() => GTM.sendEvent(EVENTS.COMMUNITY_JOINED_NOTIFS_DISPLAYED), [])

  const handleNoThanks = () => {
    GTM.sendEvent(EVENTS.COMMUNITY_JOINED_NOTIFS_REFUSED)
    editNotificationSettings({ [id]: { push: false } })
    goToNextStep()
  }

  // Join steps need me (behavioral segment) before goToNextStep can choose email vs congrats.
  if (isPending) return <ActivityIndicator />
  if (!shouldBeDisplayed) return null

  return (
    <SubscribeJourneyLayout
      testID={TEST_ID.COMMUNITY.NOTIFICATION_PERMISSIONS_SCREEN}
      stepperOptions={stepperOptions}
      icon={ICON.BELL}
      title={
        <FormattedMessage
          id="community.notifications.receive-publications-push"
          values={{ lifetime: community?.lifetime }}
        />
      }
      subtitle={
        <FormattedMessage
          id="community.notifications.receive-publications-push.subtitle"
          values={{ lifetime: community?.lifetime }}
        />
      }
      subtitleTestId={TEST_ID.COMMUNITY.NOTIFICATIONS.SUBTITLE}
      onBack={router.back}
      footer={
        <View className="gap-4">
          <PushRequest eventLabel={PUSH_EVENT_LABEL.CTA_ACCEPT_NOTIF} onPermissionChanged={goToNextStep}>
            {({ isLoading, onTrigger }) => (
              <Button
                isLoading={isLoading}
                colors="primary"
                variant="solid"
                fullWidth
                onPress={() => {
                  GTM.sendEvent(EVENTS.COMMUNITY_JOINED_NOTIFS_ACCEPTED)
                  editNotificationSettings({ [id]: { push: true } })
                  onTrigger()
                }}
              >
                <FormattedMessage id="community.notifications.activate-push" />
              </Button>
            )}
          </PushRequest>
          <Button colors="primary" variant="faded" className="w-full" onPress={handleNoThanks}>
            <FormattedMessage id="words.no-thanks2" />
          </Button>
        </View>
      }
    >
      <View className="max-h-[300px] w-full items-center rounded-lg px-10" style={{ aspectRatio: 1 }}>
        <Image
          className="max-w-screen-xs md:max-w-screen-sm"
          radius="lg"
          source={imageSource}
          onLoad={({ source }) => {
            if (!width) {
              setWidth(source.width)
            }
            if (!height) {
              setHeight(source.height)
            }
          }}
          placeholder={{ blurhash: 'LmLg9W-oNGt7~Cs.ofWC4:RkfRR*', isAnimated: true }}
        />
      </View>
    </SubscribeJourneyLayout>
  )
}
